import type {
  ControllerKind,
  DynamicControllerComponentSpec,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import type { PlayersFormFields } from "../shared/PlayersForm";
import type { SingleDateFormFields } from "../shared/SingleDateForm";
import type { SinglePlayerFormFields } from "../shared/SinglePlayerForm";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";
import { SINGLE_DATE_DEFAULTS } from "../shared/SingleDateForm";
import { SINGLE_PLAYER_DEFAULTS } from "../shared/SinglePlayerForm";
import { DEFAULT_SLICE_COLOR, nextSliceColor } from "../../Processing/controllerBindings";
import { KEY_RE, deriveKey, normalizeKey } from "../../Utils/slicerKeys.mjs";

// ── State ──

/**
 * The configurator only chooses the LABEL and which pickers to show. The picker
 * VALUES are set on the controller itself once it is on the dashboard, so they are not
 * editable here — but the state still carries them, seeded from the shared defaults and
 * round-tripped from an existing spec, because `formStateToSpec` builds the whole builder
 * misc the engine reads.
 */
export interface DynamicControllerFormState
  extends DateRangeFormFields, PlayersFormFields, SingleDateFormFields, SinglePlayerFormFields {
  label: string;
  /** Not editable: a new slicer's key follows its label, and a slicer on the board keeps its key. */
  key: string;
  slice_color: string;
  controls: ControllerKind[];
  expandedKey: string | string[];
}

// ── Actions ──

export type DynamicControllerAction =
  | { type: "SET_LABEL"; label: string; key?: string }
  | { type: "SET_SLICE_COLOR"; slice_color: string }
  | { type: "SET_CONTROLS"; controls: ControllerKind[] }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] };

// ── Reducer ──

export function dynamicControllerReducer(
  state: DynamicControllerFormState,
  action: DynamicControllerAction,
): DynamicControllerFormState {
  switch (action.type) {
    case "SET_LABEL": return { ...state, label: action.label, key: action.key ?? state.key };
    case "SET_SLICE_COLOR": return { ...state, slice_color: action.slice_color };
    case "SET_CONTROLS": return { ...state, controls: action.controls };
    case "SET_EXPANDED_KEY": return { ...state, expandedKey: action.key };
    default: return state;
  }
}

// ── Key checks ──

export const KEY_PATTERN_MESSAGE = "Use 1 to 4 letters or digits.";

/**
 * Why the key cannot be saved, or null when it can. `takenKeys` are the other
 * slicers' keys on the board (upper-cased); the compare ignores case.
 */
export function keyProblem(key: string, takenKeys: readonly string[]): string | null {
  const normalized = normalizeKey(key);
  if (!KEY_RE.test(normalized)) return KEY_PATTERN_MESSAGE;
  if (takenKeys.some((taken) => normalizeKey(taken) === normalized)) {
    return `Another slicer already uses the key ${normalized}.`;
  }
  return null;
}

// ── Factories ──

// Boards saved before the rename still carry "Slicer N" labels; they keep
// their number so a new slicer does not collide with them.
const NUMBERED_LABEL_RE = /^(?:Slicer|Slicer) (\d+)$/;

/**
 * "Slicer 1", "Slicer 2", … — the lowest number not already
 * taken, so deleting one frees its number instead of counting up forever. A renamed
 * controller drops out of the sequence, which is what makes the number reusable.
 */
export function nextControllerLabel(existingLabels: string[]): string {
  const taken = new Set(
    existingLabels
      .map((label) => NUMBERED_LABEL_RE.exec(label.trim())?.[1])
      .filter(Boolean)
      .map(Number),
  );
  let n = 1;
  while (taken.has(n)) n += 1;
  return `Slicer ${n}`;
}

export function createInitialState(
  existingLabels: string[] = [],
  existingColors: string[] = [],
  existingKeys: string[] = [],
): DynamicControllerFormState {
  const label = nextControllerLabel(existingLabels);
  return {
    label,
    key: deriveKey(label, existingKeys),
    slice_color: nextSliceColor(existingColors),
    // Exactly one picker per controller — "date" is the radio's starting choice.
    controls: ["date"],
    expandedKey: ["__date_range"],
    ...DATE_RANGE_DEFAULTS,
    ...PLAYERS_DEFAULTS,
    ...SINGLE_DATE_DEFAULTS,
    ...SINGLE_PLAYER_DEFAULTS,
  };
}

/**
 * `boardKey` is the key the board reads for this slicer (`assignKeys`), which its tokens
 * quote, so a slicer on the board keeps it. A spec that is not on the board keeps a valid
 * saved key or derives one free of `otherKeys`, the keys of the other slicers.
 */
export function hydrateFromSpec(
  spec: DynamicControllerComponentSpec,
  otherKeys: readonly string[] = [],
  boardKey: string | null = null,
): DynamicControllerFormState {
  const misc = spec.slicer_builder_misc ?? {};
  const label = misc.label || spec.display?.title || "Slicer";
  const savedKey = normalizeKey(misc.key);
  return {
    label,
    key: boardKey ?? (KEY_RE.test(savedKey) ? savedKey : deriveKey(label, otherKeys)),
    slice_color: misc.slice_color || DEFAULT_SLICE_COLOR,
    controls: spec.controls?.length ? spec.controls : ["date"],
    expandedKey: [],
    dateRange: misc.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
    showAdvancedDate: misc.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
    startDateType: misc.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
    startDateAbsolute: misc.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
    startDateOffset: misc.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
    startDateLatestN: misc.startDateLatestN ?? DATE_RANGE_DEFAULTS.startDateLatestN,
    endDateType: misc.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
    endDateAbsolute: misc.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
    endDateOffset: misc.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
    dayFilter: misc.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
    selectedSubjects: misc.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
    selectedDrills: misc.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
    subjectActiveTab: misc.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
    dateMode: misc.dateMode ?? SINGLE_DATE_DEFAULTS.dateMode,
    dateOffset: misc.dateOffset ?? SINGLE_DATE_DEFAULTS.dateOffset,
    fixedDate: misc.fixedDate ?? SINGLE_DATE_DEFAULTS.fixedDate,
    selectedPlayer: misc.selectedPlayer ?? SINGLE_PLAYER_DEFAULTS.selectedPlayer,
  };
}

/**
 * Null while the key cannot be saved (it fails the pattern or another slicer on the
 * board holds it), which disables Add Widget and Save Changes, as the title and text
 * configurators do for an invalid token.
 */
export function formStateToSpec(
  state: DynamicControllerFormState,
  _metrics: MetricOption[],
  getSubjectEntities?: (t: string) => SubjectEntity[],
  takenKeys: readonly string[] = [],
): DynamicControllerComponentSpec | null {
  if (keyProblem(state.key, takenKeys)) return null;

  const selectedSubjectNames = getSubjectEntities
    ? resolveSubjectNames(state.selectedSubjects, state.selectedDrills, getSubjectEntities)
    : [];

  const misc = {
    label: state.label,
    key: normalizeKey(state.key),
    slice_color: state.slice_color,
    dateRange: state.dateRange,
    showAdvancedDate: state.showAdvancedDate,
    startDateType: state.startDateType,
    startDateLatestN: state.startDateLatestN,
    startDateAbsolute: state.startDateAbsolute,
    startDateOffset: state.startDateOffset,
    endDateType: state.endDateType,
    endDateAbsolute: state.endDateAbsolute,
    endDateOffset: state.endDateOffset,
    dayFilter: state.dayFilter,
    selectedSubjects: state.selectedSubjects,
    selectedDrills: state.selectedDrills,
    subjectActiveTab: state.subjectActiveTab,
    selectedSubjectNames,
    dateMode: state.dateMode,
    dateOffset: state.dateOffset,
    fixedDate: state.fixedDate,
    selectedPlayer: state.selectedPlayer,
  };

  // The controller stores only its picker state. The DashboardEngine derives the date
  // range and subject list from these fields when it resolves a governed query.
  return {
    type: "slicer",
    display: { title: state.label || null },
    controls: state.controls,
    slicer_builder_misc: misc,
  };
}
